/**
 * Learning-path access, on the person page — the LEARNER-FIRST way into the audited
 * free-enrolment cancellation.
 *
 * WHY THIS CARD IS SHAPED LIKE A QUESTION AND NOT LIKE A LIST
 * ----------------------------------------------------------
 * An academy admin arriving from a support request starts with the person, not with the path. So
 * this belongs here. What does NOT exist is a read that answers "which learning paths is this
 * person on": `GET /api/pathways/v1/learner/paths` is self-only, `academy/enrolments` is COURSE
 * enrolments, and the only per-path roster is `paths/<key>/learners`, which would be one request
 * per path in the academy. So the card does not claim to list the person's pathway enrolments,
 * because it cannot, and it says so rather than showing an empty table that reads as "none".
 *
 * WHAT IT DOES INSTEAD, AND THE INFERENCE IS LABELLED AS ONE. The academy's own path listing is
 * one bounded request, already cached under `['pathways-admin']` by the Learning Paths screen,
 * and it carries every path's `steps`. A path whose courses this person is enrolled in is a
 * CANDIDATE — marked as inferred from course overlap, in the same vocabulary the backend uses
 * for `inferred_temporal`, and never presented as a record of enrolment. The authority is the
 * preview: `free-enrolments/preview` answers `not_enrolled` when they are not on it and
 * `paid_enrolment_not_free` when the enrolment was bought, and both refusals already have their
 * own sentence in the dialog.
 *
 * EVERY PATH IS SELECTABLE, not only the candidates. The overlap heuristic misses the case that
 * matters most for this operation — a pathway enrolment whose member courses were all removed
 * already, or one whose courses the learner holds under a different path — so restricting the
 * picker to candidates would hide exactly the enrolments somebody is trying to clean up.
 *
 * AUTHORITY IS THE SERVER'S `writable_orgs`, NOT THE PERSONA. A Host may legitimately serve
 * another academy's org so its learners can take those courses, and that academy's paths appear
 * in this listing. `canWrite` is the pathways app's own gate and it is the one used here; the
 * host's org list is deliberately wider than this academy's, so deciding from it would offer
 * controls over another academy's pathway. The payment-ops gate is separate and
 * `FreeEnrolmentAction` asks it for itself.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Banner, Card, SelectField, Spinner } from '../../shared/ui';
import {
  canWrite,
  listAdminPaths,
  pathwayProblem,
  type AdminPath,
} from '../../shared/lib/pathways-admin';
import type { Member } from '../../shared/lib/team';
import { FreeEnrolmentAction } from '../payments/FreeEnrolmentAction';

/** The same key the Learning Paths screen uses, so arriving from it costs no request. */
const PATHS_QUERY = ['pathways-admin'];

/** How many of this person's enrolled courses a path contains. Zero means no signal either way. */
export function overlapCount(path: AdminPath, courseKeys: ReadonlySet<string>): number {
  return (path.steps ?? []).filter((step) => courseKeys.has(step.course_key)).length;
}

export function LearningPathAccess({
  person,
  /**
   * Whether this viewer administers this academy at all — the members list's own answer.
   *
   * A `false` here removes the card entirely rather than showing it read-only: a learner
   * looking at their own page has no business reading the academy's whole path catalogue.
   */
  canManage,
}: {
  person: Member;
  canManage: boolean;
}) {
  const [chosen, setChosen] = useState('');

  /**
   * `enabled: canManage` and NOT an early return above the hook.
   *
   * Rules of hooks forbid returning before `useQuery`, so the naive version issued the request
   * and then rendered nothing with the answer — a learner viewing their own page fetching the
   * academy's whole path catalogue. `enabled` is what makes "not for you" mean no request.
   */
  const paths = useQuery({
    queryKey: PATHS_QUERY,
    queryFn: listAdminPaths,
    enabled: canManage,
    retry: false,
  });

  const courseKeys = useMemo(
    () => new Set((person.enrolments ?? []).map((row) => row.courseId)),
    [person.enrolments],
  );

  const editable = useMemo(() => {
    const all = paths.data?.results ?? [];
    const writableOrgs = paths.data?.writable_orgs ?? [];
    return all
      .filter((path) => canWrite(path, writableOrgs))
      .map((path) => ({ path, overlap: overlapCount(path, courseKeys) }))
      // Candidates first, then by how much of the path this person holds, then by name — so the
      // most likely answer is at the top of the list without anything else being hidden.
      .sort((a, b) => b.overlap - a.overlap || a.path.display_name.localeCompare(b.path.display_name));
  }, [paths.data, courseKeys]);

  if (!canManage) return null;

  if (paths.isPending) {
    return (
      <Card title="Learning-path access">
        <Spinner label="Loading this academy's learning paths…" />
      </Card>
    );
  }

  if (paths.isError) {
    // The server's own sentence. `pathwayProblem` is the pathways surface's existing reader and
    // it is used rather than a local paraphrase, so a refusal reads the same here as there.
    const problem = pathwayProblem(paths.error, 'learning path');
    return (
      <Card title="Learning-path access">
        <Banner tone="warning">
          <p>
            <strong>{problem.headline}</strong> {problem.detail}
          </p>
          <p>
            Nothing is claimed about what this person holds — this is a failure to look, not an
            answer.
          </p>
        </Banner>
      </Card>
    );
  }

  if (editable.length === 0) {
    return (
      <Card title="Learning-path access">
        <p className="team__quiet">
          This academy has no learning paths you administer, so there is no path enrolment to
          cancel from here.
        </p>
      </Card>
    );
  }

  const candidates = editable.filter((row) => row.overlap > 0);
  const selected = editable.find((row) => row.path.key === chosen);

  return (
    <Card title="Learning-path access">
      <p className="team__quiet">
        <strong>
          This academy&rsquo;s API does not report which learning paths a person is enrolled on.
        </strong>{' '}
        So this is not a list of their enrolments. Choose the path and the academy will answer
        whether they hold it, what cancelling would remove, and what it would keep.
      </p>

      {candidates.length > 0 ? (
        <>
          <h4>Likely, from course overlap</h4>
          <p className="team__quiet">
            Inferred from the courses they are enrolled in — the same kind of circumstantial
            evidence the cancellation itself refuses to act on without confirmation. Not a record
            of a path enrolment.
          </p>
          <ul className="people__courses">
            {candidates.map(({ path, overlap }) => (
              <li key={path.key}>
                {path.display_name}{' '}
                <Badge tone="warning">
                  {overlap} of {(path.steps ?? []).length} courses
                </Badge>
                <span className="mono">{path.key}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="team__quiet">
          None of this academy&rsquo;s paths contains a course they are enrolled in. That is not
          the same as holding no path enrolment — a path whose courses were already removed
          leaves no overlap behind.
        </p>
      )}

      <SelectField
        label="Learning path"
        name="free-cancel-path"
        value={chosen}
        onChange={(event) => setChosen(event.target.value)}
        options={[
          ['', 'Choose a learning path…'],
          ...editable.map(
            ({ path, overlap }) =>
              [
                path.key,
                overlap > 0 ? `${path.display_name} — ${overlap} course(s) overlap` : path.display_name,
              ] as [string, string],
          ),
        ]}
      />

      {selected && (
        <FreeEnrolmentAction
          pathwayKey={selected.path.key}
          username={person.username}
          email={person.email}
        >
          <p className="team__quiet">
            Cancelling removes the pathway and the courses it can be proven to have granted.
            Anything {person.username} owns independently of it — bought separately, granted by
            another path, or held before — is kept, and you will see which before confirming.
          </p>
        </FreeEnrolmentAction>
      )}
    </Card>
  );
}
